<script setup lang="ts">
import { dashboardApiClient } from '@api-client/dashboard';
import type { ResType } from '@api-client/typeUtils';
import { computed, onMounted, ref } from 'vue';
import { formatCurrency } from '@lib/utils/formatCurrency';
import { toast } from '@/ui-plus/sonner';
import { currentWebsiteId } from '@partner-spa-vue/stores/MyWebsitesStore';
import DebugSupportIncomingActions from './DebugSupportIncomingActions.vue';

type IncomingResponse = ResType<
    typeof dashboardApiClient.website.partner_support.store_incoming[':websiteId']['$get']
>["data"];
type DeliveryteamIncomingResponse = ResType<
    typeof dashboardApiClient.website.partner_support.deliveryteam_incoming[':websiteId']['$get']
>["data"];

const incoming = ref<IncomingResponse | null>(null);
const deliveryteamIncoming = ref<DeliveryteamIncomingResponse | null>(null);

const totalsByCurrency = computed(() => {
    const totals = new Map<string, number>();
    for (const row of [...(incoming.value ?? []), ...(deliveryteamIncoming.value ?? [])]) {
        totals.set(row.currency, (totals.get(row.currency) ?? 0) + Number(row.amount));
    }
    return [...totals.entries()].map(([currency, amount]) => ({ currency, amount }));
});

const loadIncoming = async () => {
    const websiteId = currentWebsiteId.value || '';
    const [storeRes, deliveryteamRes] = await Promise.all([
        (await dashboardApiClient.website.partner_support.store_incoming[':websiteId'].$get({
            param: { websiteId }
        })).json(),
        (await dashboardApiClient.website.partner_support.deliveryteam_incoming[':websiteId'].$get({
            param: { websiteId }
        })).json(),
    ]);
    if (storeRes.error) {
        console.error(storeRes.error);
        toast.error('Failed to load incoming data');
    } else {
        incoming.value = [...storeRes.data].sort((a, b) => a.date.localeCompare(b.date));
    }
    if (deliveryteamRes.error) {
        console.error(deliveryteamRes.error);
        toast.error('Failed to load deliveryteam incoming data');
    } else {
        deliveryteamIncoming.value = [...deliveryteamRes.data].sort((a, b) => a.date.localeCompare(b.date));
    }
};

onMounted(loadIncoming);
</script>

<template>
    <div class="mt-8"
         v-if="incoming">
        <h2 class="text-lg font-bold">Incoming</h2>
        <p class="text-sm text-muted-foreground">
            Funds expected to be settled soon into your balance.
        </p>
        <div v-if="incoming.length === 0 && (!deliveryteamIncoming || deliveryteamIncoming.length === 0)"
             class="text-center py-8 text-muted-foreground">
            No incoming earnings found
        </div>
        <div v-else
             class="space-y-3">

            <div v-if="totalsByCurrency.length > 0"
                 class="pt-2">
                <h3 class="font-medium mt-2 mb-2">Total</h3>
                <div class="space-y-3">
                    <div v-for="total in totalsByCurrency"
                         :key="total.currency"
                         class="flex items-center justify-between p-3 border rounded-lg">
                        <p class="font-medium">{{ total.currency.toUpperCase() }}</p>
                        <div class="text-right text-lg font-bold">
                            {{ formatCurrency(total.amount, total.currency) }}
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="incoming.length > 0">
                <h3 class="font-medium mt-8 mb-2">Stores</h3>
                <div class="space-y-3">
                    <div v-for="daily in incoming"
                         :key="daily.date"
                         class="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                            <p class="font-medium">
                                Settlement Date:
                                {{ daily.date }}
                            </p>
                        </div>
                        <div class="text-right text-lg font-bold">
                            {{ formatCurrency(daily.amount, daily.currency) }}
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="deliveryteamIncoming && deliveryteamIncoming.length > 0">
                <h3 class="font-medium mt-8 mb-2">Delivery teams</h3>
                <div class="space-y-3">
                    <div v-for="daily in deliveryteamIncoming"
                         :key="daily.date"
                         class="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                            <p class="font-medium">
                                Settlement Date:
                                {{ daily.date }}
                            </p>
                        </div>
                        <div class="text-right text-lg font-bold">
                            {{ formatCurrency(daily.amount, daily.currency) }}
                        </div>
                    </div>
                </div>
            </div>

        </div>
        <DebugSupportIncomingActions :website-id="currentWebsiteId || ''"
                                     @reload="loadIncoming" />
    </div>
    <div v-else>
        Loading...
    </div>
</template>
